export const useGeoLocation = () => {
  const route = useRoute()
  const userStore = useUserStore()
  const runtimeConfig = useRuntimeConfig()

  // Create a state variable that is initialized with the request headers on the server.
  // useState is serialized into the page payload, so whitelist the two geo
  // headers — the full set includes the visitor's IP (x-real-ip,
  // x-forwarded-for) and cookies.
  const geoHeaders = useState("geoHeaders", () => {
    // Only on the server will useRequestHeaders() be available
    return import.meta.server
      ? useRequestHeaders([
          "cloudfront-viewer-country",
          "cloudfront-viewer-country-region",
        ])
      : {}
  })

  const getGeoLocation = () => {
    if (route.query.geo) {
      // TODO: Don't allow query manipulation in production
      return route.query.geo.toUpperCase()
    } else {
      // Prefer live CloudFront headers (present on SSR requests). Fall back to
      // the member's login-time geo persisted in the user store — the only geo
      // signal available on ssr:false pages (e.g. the members area), which have
      // no request headers. userStore.country is already unwrapped by Pinia, so
      // there is no `.value` (the old `userStore?.country?.value` was always
      // undefined, so the store fallback never applied).
      const country =
        geoHeaders.value?.["cloudfront-viewer-country"] ||
        userStore.country ||
        "US"
      const region =
        geoHeaders.value?.["cloudfront-viewer-country-region"] ||
        userStore.region
      return region ? `${country}-${region}` : country
    }
  }

  const ageRestrictedAreas = [
    "AU",
    "DE",
    "FR",
    "GB",
    "UK",
    "US-AL",
    "US-AR",
    "US-AZ",
    "US-FL",
    "US-GA",
    "US-IA",
    "US-ID",
    "US-IN",
    "US-KS",
    "US-KY",
    "US-LA",
    "US-MO",
    "US-MS",
    "US-MT",
    "US-NC",
    "US-NE",
    "US-OH",
    "US-OK",
    "US-SC",
    "US-SD",
    "US-TN",
    "US-TX",
    "US-UT",
    "US-VA",
    "US-WV",
    "US-WY",
  ]

  const location = getGeoLocation()
  const splitLocation = location.split("-")
  const country = splitLocation[0]
  const region = splitLocation.length > 1 ? splitLocation[1] : null

  // Fri 22:00 UTC (5 PM EST) → Mon 13:00 UTC (8 AM EST). UTC-5 fixed, no DST.
  const isInsideWeekendNsfwWindow = (now = new Date()) => {
    const day = now.getUTCDay()
    const hour = now.getUTCHours()
    if (day === 5 && hour >= 22) return true
    if (day === 6 || day === 0) return true
    if (day === 1 && hour < 13) return true
    return false
  }

  // US state restrictions relax during the weekend NSFW window; AU/DE/FR/GB/UK
  // are country-level and always restricted.
  const isCountryRestriction = ageRestrictedAreas.includes(country)
  const isUsStateRestriction =
    country === "US" && ageRestrictedAreas.includes(location)

  const weekendWindowEnabled =
    runtimeConfig.public.weekendNsfwWindowEnabled !== false
  const weekendExemption = weekendWindowEnabled && isInsideWeekendNsfwWindow()

  // Visitors who have completed age verification via Private AV carry an
  // age_verified=1 cookie (180 days, HttpOnly, site-domain-scoped). The cookie
  // is HttpOnly so it can't be read via document.cookie/useCookie on the
  // client — read it on the server from the request Cookie header and persist
  // through hydration via useState.
  const isAgeVerified = useState("ageVerified", () => {
    if (!import.meta.server) return false
    const cookieHeader = useRequestHeaders(["cookie"]).cookie || ""
    return cookieHeader
      .split(";")
      .map((c) => c.trim())
      .some((c) => c === "age_verified=1")
  })

  const inAgeRestrictedLocation =
    !isAgeVerified.value &&
    (isCountryRestriction || (isUsStateRestriction && !weekendExemption))

  return {
    country,
    region: region || null,
    location,
    inAgeRestrictedLocation,
  }
}
